import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Sidebar from "../components/Sidebar";
import FixedFeeReport, { exportStatementsExcel, money, monthLabel } from "../components/FixedFeeReport";
import {
  FIXED_FEE_CLIENTS, SECTIONS, emptyStatement, grandTotals, normalizeStatement, statementTotals, withCumulative
} from "../services/fixedFeeCalculator";
import { buildTemplateWorkbook, parseStatementSheet } from "../services/fixedFeeSheet";
import {
  canEditFixedFee, createShare, deleteStatement, disableShare, fetchInvoicedFee, getShare, listStatements, publishShare, saveStatement
} from "../services/fixedFeeService";

const CLIENT = FIXED_FEE_CLIENTS[0];
const previousMonth = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1)).toISOString().slice(0, 7);
};
const signed = (value) => (Math.round(value) < 0 ? `(${money(Math.abs(value))})` : money(value));
const asDraft = (saved, period) => saved
  ? { ...emptyStatement(CLIENT.code, period), ...saved, fee: String(saved.fee ?? ""), serviceCharge: String(saved.serviceCharge ?? ""), taxCollected: saved.taxCollected == null ? "" : String(saved.taxCollected), whtWithheld: saved.whtWithheld == null ? "" : String(saved.whtWithheld), taxWithheld: saved.taxWithheld == null ? "" : String(saved.taxWithheld), lines: (saved.lines || []).map((line) => ({ ...line, amount: String(line.amount) })) }
  : { ...emptyStatement(CLIENT.code, period), serviceCharge: String(CLIENT.defaultServiceCharge), taxCollected: "", whtWithheld: "", taxWithheld: "" };

export default function FixedFeeStatement() {
  const editor = canEditFixedFee();
  const [period, setPeriod] = useState(previousMonth);
  const [statements, setStatements] = useState([]);
  const [draft, setDraft] = useState(() => asDraft(null, previousMonth()));
  const [share, setShare] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [upload, setUpload] = useState(null);

  const load = useCallback(async () => {
    try {
      const [list, link] = await Promise.all([listStatements(CLIENT.code), getShare(CLIENT.code)]);
      setStatements(list); setShare(link);
      return list;
    } catch (err) { setError(err.message || "Could not load the statements."); return []; }
  }, []);

  useEffect(() => { load().then((list) => setDraft(asDraft(list.find((item) => item.period === period), period))); }, []); // eslint-disable-line

  const saved = statements.find((item) => item.period === period) || null;
  const dirty = useMemo(() => JSON.stringify(normalizeStatement({ ...draft, taxRate: draft.taxRate }))
    !== JSON.stringify(normalizeStatement(asDraft(saved, period))), [draft, saved, period]);

  const notify = (text, isError = false) => { setError(isError ? text : ""); setMessage(isError ? "" : text); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const openMonth = (next) => {
    if (next === period) return;
    if (dirty && !window.confirm("You have unsaved changes for this month. Discard them?")) return;
    setPeriod(next);
    setDraft(asDraft(statements.find((item) => item.period === next), next));
  };

  const set = (field) => (e) => setDraft((current) => ({ ...current, [field]: e.target.value }));
  const setLine = (index, field, value) => setDraft((current) => ({ ...current, lines: current.lines.map((line, i) => (i === index ? { ...line, [field]: value } : line)) }));
  const addLine = (section) => setDraft((current) => ({ ...current, lines: [...current.lines, { section, description: "", amount: "", note: "" }] }));
  const removeLine = (index) => setDraft((current) => ({ ...current, lines: current.lines.filter((_, i) => i !== index) }));

  const totals = statementTotals({ ...draft, taxRate: Number(draft.taxRate), whtRate: Number(draft.whtRate), taxWithholdingRate: Number(draft.taxWithholdingRate) });
  const pct = (rate) => Math.round(Number(rate || 0) * 1000) / 10;
  const taxPercent = pct(draft.taxRate);

  const fillFromInvoice = async () => {
    try {
      const { amount, invoiceNumbers } = await fetchInvoicedFee(CLIENT.code, period);
      if (!amount) { notify(`No approved ${CLIENT.code} invoice found for ${monthLabel(period)}.`, true); return; }
      setDraft((current) => ({ ...current, fee: String(amount) }));
      notify(`Fee set from invoice ${invoiceNumbers.join(", ")}.`);
    } catch (err) { notify(err.message || "Could not read the invoice.", true); }
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await saveStatement({ ...draft, taxRate: Number(draft.taxRate), whtRate: Number(draft.whtRate), taxWithholdingRate: Number(draft.taxWithholdingRate) });
      await load();
      notify(`${monthLabel(period)} saved. It is not visible to the client until you publish.`);
    } catch (err) { notify(err.message || "Could not save.", true); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the saved statement for ${monthLabel(period)}? Publish afterwards to remove it from the client's link.`)) return;
    try { await deleteStatement(CLIENT.code, period); const list = await load(); setDraft(asDraft(list.find((item) => item.period === period), period)); notify("Statement deleted."); }
    catch (err) { notify(err.message || "Could not delete.", true); }
  };

  const publishedKey = share ? JSON.stringify(share.statements || []) : "";
  const currentKey = JSON.stringify(statements.map(normalizeStatement).sort((a, b) => a.period.localeCompare(b.period)));
  const unpublished = Boolean(share) && publishedKey !== currentKey;
  const link = share ? `${window.location.origin}/share/cpbm/${share.token}` : "";

  const runShare = async (action, success) => {
    setBusy(true);
    try { await action(); await load(); notify(success); }
    catch (err) { notify(err.message || "Sharing failed.", true); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    saveAs(new Blob([XLSX.write(buildTemplateWorkbook(), { bookType: "xlsx", type: "array" })], { type: "application/octet-stream" }), `${CLIENT.code}_Monthly_Statement_Template.xlsx`);
  };

  const chooseSheet = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const result = parseStatementSheet(await file.arrayBuffer(), CLIENT.code);
      setUpload({ ...result, fileName: file.name });
    } catch (err) { setUpload(null); notify(err.message, true); }
    event.target.value = "";
  };

  const importSheet = async () => {
    const existing = new Set(statements.map((item) => item.period));
    const replacing = upload.statements.filter((item) => existing.has(item.period)).map((item) => monthLabel(item.period));
    if (replacing.length && !window.confirm(`These months are already saved and will be replaced: ${replacing.join(", ")}. Continue?`)) return;
    setBusy(true);
    try {
      for (const item of upload.statements) await saveStatement(item);
      const list = await load();
      setDraft(asDraft(list.find((item) => item.period === period), period));
      setUpload(null);
      notify(`${upload.statements.length} month(s) imported. Review them below, then publish when you are happy for the client to see them.`);
    } catch (err) { notify(err.message || "Import failed part-way — check the months list and re-upload.", true); }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link); notify("Link copied."); }
    catch (err) { notify("Copy failed — select the link and copy it manually.", true); }
  };

  const rows = withCumulative(statements);
  const all = grandTotals(statements);

  const sectionEditor = (section) => (
    <div key={section} style={{ marginTop: 18 }}>
      <div className="panel-header" style={{ padding: 0, marginBottom: 6 }}>
        <h3 style={{ margin: 0 }}>{SECTIONS[section]}</h3>
        <button type="button" className="btn btn-muted" onClick={() => addLine(section)}>Add line</button>
      </div>
      <div className="table-wrap">
        <table className="data-table">
          <thead><tr><th>Description</th><th>Amount (PKR)</th><th>Note</th><th /></tr></thead>
          <tbody>
            {draft.lines.map((line, index) => line.section !== section ? null : (
              <tr key={index}>
                <td><input className="input" value={line.description} onChange={(e) => setLine(index, "description", e.target.value)} placeholder={section === "SALARY" ? "e.g. Salaries — actual working days" : section === "TAX" ? "e.g. PRA sales tax" : "e.g. Uniform / stationery"} /></td>
                <td><input className="input" style={{ width: 150 }} type="number" min="0" value={line.amount} onChange={(e) => setLine(index, "amount", e.target.value)} /></td>
                <td><input className="input" value={line.note} onChange={(e) => setLine(index, "note", e.target.value)} /></td>
                <td><button type="button" className="btn btn-danger" onClick={() => removeLine(index)}>Remove</button></td>
              </tr>
            ))}
            {!draft.lines.some((line) => line.section === section) && <tr><td colSpan={4}>No lines yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (!editor) return <div className="app-shell"><Sidebar /><main className="main-content"><div className="management-alert error">You do not have access to this page.</div></main></div>;

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Clients</div>
            <h1 className="page-title">{CLIENT.code} Fixed-Fee Statement</h1>
            <p className="page-copy">
              {CLIENT.name} pays a fixed fee every month. Salaries are paid on actual working days, so less than the fee is spent and the balance is saved with ALM.
              Record each month here and share the working with the client.
            </p>
          </div>
          <div className="field pnl-print-hide" style={{ minWidth: 180 }}>
            <label>Month</label>
            <input className="input" type="month" value={period} onChange={(e) => e.target.value && openMonth(e.target.value)} />
          </div>
        </header>

        {(message || error) && <div className={`management-alert pnl-print-hide ${error ? "error" : "success"}`}>{error || message}</div>}

        <section className="management-metrics pnl-print-hide">
          <article className="metric-card"><div className="metric-label">Months recorded</div><div className="metric-value">{all.months}</div></article>
          <article className="metric-card"><div className="metric-label">Cash received</div><div className="metric-value">{money(all.received)}</div></article>
          <article className="metric-card"><div className="metric-label">Total paid out</div><div className="metric-value">{money(all.paidOut)}</div></article>
          <article className="metric-card"><div className="metric-label">Total saved with ALM</div><div className="metric-value">{signed(all.saved)}</div></article>
        </section>

        <section className="panel pnl-print-hide">
          <div className="panel-header"><h2 className="panel-title">{monthLabel(period)} {saved ? "" : "— not saved yet"}</h2></div>
          <form className="panel-body" onSubmit={save}>
            <div className="form-grid">
              <div className="field"><label>Fixed monthly fee, before tax (PKR)</label>
                <input className="input" type="number" min="0" value={draft.fee} onChange={set("fee")} />
                <button type="button" className="btn btn-muted" style={{ marginTop: 6 }} onClick={fillFromInvoice}>Fill from approved invoice</button>
              </div>
              <div className="field"><label>Tax rate (%)</label>
                <input className="input" type="number" step="0.1" value={taxPercent} onChange={(e) => setDraft({ ...draft, taxRate: Number(e.target.value) / 100 })} />
              </div>
              <div className="field"><label>Tax collected (PKR) — blank = rate × fee</label>
                <input className="input" type="number" min="0" value={draft.taxCollected} placeholder={String(totals.taxCollected)} onChange={set("taxCollected")} />
              </div>
              <div className="field"><label>Income tax (WHT) the client deducts (%)</label>
                <input className="input" type="number" step="0.1" value={pct(draft.whtRate)} onChange={(e) => setDraft({ ...draft, whtRate: Number(e.target.value) / 100 })} />
              </div>
              <div className="field"><label>WHT amount (PKR) — blank = rate × invoice</label>
                <input className="input" type="number" min="0" value={draft.whtWithheld} placeholder={String(totals.whtWithheld)} onChange={set("whtWithheld")} />
              </div>
              <div className="field"><label>Sales tax the client withholds (%)</label>
                <input className="input" type="number" step="0.1" value={pct(draft.taxWithholdingRate)} onChange={(e) => setDraft({ ...draft, taxWithholdingRate: Number(e.target.value) / 100 })} />
              </div>
              <div className="field"><label>Sales tax withheld (PKR) — blank = rate × tax</label>
                <input className="input" type="number" min="0" value={draft.taxWithheld} placeholder={String(totals.taxWithheld)} onChange={set("taxWithheld")} />
              </div>
              <div className="field"><label>Fixed service charge (PKR)</label>
                <input className="input" type="number" min="0" value={draft.serviceCharge} onChange={set("serviceCharge")} />
              </div>
            </div>

            {Object.keys(SECTIONS).map(sectionEditor)}

            <div className="field" style={{ marginTop: 16 }}><label>Note for the client (optional)</label>
              <input className="input" value={draft.notes} onChange={set("notes")} />
            </div>

            <table className="data-table" style={{ maxWidth: 520, marginTop: 16 }}>
              <tbody>
                <tr><td>Fixed fee</td><td>{money(totals.fee)}</td></tr>
                <tr><td>+ Sales tax charged</td><td>{money(totals.taxCollected)}</td></tr>
                <tr style={{ fontWeight: 700 }}><td>Total invoiced</td><td>{money(totals.invoiced)}</td></tr>
                <tr><td>− Income tax (WHT) withheld by client</td><td>{money(totals.whtWithheld)}</td></tr>
                <tr><td>− Sales tax withheld by client</td><td>{money(totals.taxWithheld)}</td></tr>
                <tr style={{ fontWeight: 700 }}><td>Cash received</td><td>{money(totals.received)}</td></tr>
                <tr><td>− Salaries</td><td>{money(totals.salaries)}</td></tr>
                <tr><td>− Expenses</td><td>{money(totals.expenses)}</td></tr>
                <tr><td>− Taxes ALM paid (PRA etc.)</td><td>{money(totals.taxesPaid)}</td></tr>
                <tr><td>− Service charge</td><td>{money(totals.serviceCharge)}</td></tr>
                <tr style={{ fontWeight: 700 }}><td>Saved this month</td><td>{signed(totals.saved)} ({totals.savedPercentOfFee}% of fee)</td></tr>
              </tbody>
            </table>

            <div className="button-row" style={{ marginTop: 16 }}>
              <button className="btn btn-primary" type="submit" disabled={busy || !dirty}>{saved ? "Save Changes" : "Save Month"}</button>
              {saved && <button className="btn btn-danger" type="button" onClick={remove}>Delete Month</button>}
              {dirty && <span className="page-copy" style={{ alignSelf: "center" }}>Unsaved changes</span>}
            </div>
          </form>
        </section>

        <section className="panel pnl-print-hide">
          <div className="panel-header">
            <h2 className="panel-title">Upload many months from a sheet</h2>
            <button type="button" className="btn btn-muted" onClick={downloadTemplate}>Download Template</button>
          </div>
          <div className="panel-body">
            <label className="file-drop"><div className="file-title">Choose the filled template (.xlsx)</div><div className="file-copy">One row per month: Fixed Fee, Salary, EOBI, Social Security, Monthly Expense, TAX PRA. Months already saved are replaced after you confirm.</div><input className="hidden-input" type="file" accept=".xlsx,.xls" onChange={chooseSheet} /></label>
            {upload && (
              <>
                {upload.errors.length > 0 && <div className="management-alert error" style={{ marginTop: 12 }}>{upload.errors.join(" ")} Fix the sheet and upload again — nothing is imported while rows have problems.</div>}
                <div className="table-wrap" style={{ marginTop: 12 }}>
                  <table className="data-table">
                    <thead><tr><th>Month</th><th>Fixed fee</th><th>Cash received</th><th>Paid out</th><th>Saved</th><th>Status</th></tr></thead>
                    <tbody>
                      {upload.statements.map((item) => (
                        <tr key={item.period}>
                          <td>{monthLabel(item.period)}</td><td>{money(item.totals.fee)}</td><td>{money(item.totals.received)}</td><td>{money(item.totals.paidOut)}</td>
                          <td><strong>{signed(item.totals.saved)}</strong></td>
                          <td>{statements.some((saved) => saved.period === item.period) ? "Replaces saved month" : "New"}</td>
                        </tr>
                      ))}
                      {!upload.statements.length && <tr><td colSpan={6}>No month with data was found in {upload.fileName}.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <p className="page-copy">Withholding rates come from the month (6% up to June 2026, 7% from July 2026) unless the sheet overrides them. {upload.ignored ? `${upload.ignored} month row(s) with no figures were ignored.` : ""}</p>
                <button type="button" className="btn btn-primary" disabled={busy || upload.errors.length > 0 || !upload.statements.length} onClick={importSheet}>Import {upload.statements.length} Month(s)</button>
              </>
            )}
          </div>
        </section>

        <section className="panel pnl-print-hide">
          <div className="panel-header"><h2 className="panel-title">All months</h2></div>
          <div className="panel-body table-wrap">
            <table className="data-table">
              <thead><tr><th>Month</th><th>Cash received</th><th>Paid out</th><th>Saved</th><th>Saved to date</th><th /></tr></thead>
              <tbody>
                {[...rows].reverse().map((row) => (
                  <tr key={row.period}><td>{monthLabel(row.period)}</td><td>{money(row.totals.received)}</td><td>{money(row.totals.paidOut)}</td><td><strong>{signed(row.totals.saved)}</strong></td><td>{signed(row.cumulativeSaved)}</td>
                    <td><button type="button" className="btn btn-muted" onClick={() => openMonth(row.period)}>Open</button></td></tr>
                ))}
                {!rows.length && <tr><td colSpan={6}>No months saved yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel pnl-print-hide">
          <div className="panel-header"><h2 className="panel-title">Share with {CLIENT.name}</h2></div>
          <div className="panel-body">
            {!share ? (
              <>
                <p className="page-copy">Create a private link. Anyone with the link can view the statement without logging in, but only what you publish. Nothing is shared until you do.</p>
                <button type="button" className="btn btn-primary" disabled={busy || !statements.length} onClick={() => runShare(() => createShare(CLIENT.code, CLIENT.name, statements), "Link created and the saved months are published.")}>Create Client Link &amp; Publish</button>
                {!statements.length && <p className="page-copy">Save at least one month first.</p>}
              </>
            ) : (
              <>
                <div className="field"><label>Client link (no login needed)</label><input className="input" readOnly value={link} onFocus={(e) => e.target.select()} /></div>
                <p className="page-copy">
                  Published {share.publishedAt?.toDate ? share.publishedAt.toDate().toLocaleString("en-GB") : ""} — {(share.statements || []).length} month(s) visible to the client.
                  {unpublished && <strong style={{ color: "var(--danger)" }}> You have changes the client cannot see yet.</strong>}
                </p>
                <div className="button-row">
                  <button type="button" className="btn btn-muted" onClick={copyLink}>Copy Link</button>
                  <button type="button" className="btn btn-primary" disabled={busy || !unpublished} onClick={() => runShare(() => publishShare(share.token, CLIENT.code, CLIENT.name, statements), "Published — the client link now shows the latest.")}>Publish Latest</button>
                  <button type="button" className="btn btn-muted" disabled={busy} onClick={() => window.confirm("Create a new link? The current link stops working immediately.") && runShare(() => createShare(CLIENT.code, CLIENT.name, statements), "New link created; the old one no longer works.")}>New Link</button>
                  <button type="button" className="btn btn-danger" disabled={busy} onClick={() => window.confirm("Turn off the client link? The client will no longer be able to open it.") && runShare(() => disableShare(share.token), "Link turned off.")}>Turn Off Link</button>
                </div>
              </>
            )}
            <div className="button-row" style={{ marginTop: 16 }}>
              <button type="button" className="btn btn-muted" disabled={!statements.length} onClick={() => window.print()}>Print / Save as PDF</button>
              <button type="button" className="btn btn-muted" disabled={!statements.length} onClick={() => exportStatementsExcel(statements, CLIENT.name)}>Download Excel</button>
            </div>
          </div>
        </section>

        <div className="print-only"><FixedFeeReport clientName={CLIENT.name} statements={statements} /></div>
      </main>
    </div>
  );
}
